import type { Middleware, UnknownAction } from "@reduxjs/toolkit";
import type { AppDispatch } from "_shared/_stores/store-types";
import SearchResults from "_shared/_api/search/search-results";
import { marketplaceApi } from "marketplace/_api/marketplace-api";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import CompanySchema from "marketplace/companies/company-schema";
import IndividualSchema from "marketplace/individuals/_schema/individual-schema";
import TeamSchema from "marketplace/teams/_schema/team-schema";

function isMarketplaceSearchFulfilledAction(action: UnknownAction): boolean {
  return (
    marketplaceApi.endpoints.searchAll.matchFulfilled(action) ||
    marketplaceApi.endpoints.searchIndividuals.matchFulfilled(action) ||
    marketplaceApi.endpoints.searchTeams.matchFulfilled(action)
  );
}

function hydrateMarketplaceSearchCaches(
  response: SearchResults<MarketplaceSearchInfoJSON>,
  dispatch: AppDispatch,
): void {
  for (const hit of response.hits ?? []) {
    try {
      const result = hit.data as MarketplaceSearchInfoJSON;

      if (result.type === "Individual" && result.id) {
        const individualJSON = IndividualSchema.validateJSON({
          ...result,
          id: result.id,
          userId: result.userId,
          legalEntityId: result.legalEntityId,
          firstName: result.firstName,
          lastName: result.lastName,
          isVisible: result.isVisible ?? false,
        });

        dispatch(
          marketplaceApi.util.upsertQueryData(
            "getMarketplaceIndividualById",
            { individualId: individualJSON.id },
            individualJSON,
          ),
        );
        continue;
      }

      if (result.type === "Team") {
        const teamJSON = TeamSchema.validateJSON({
          ...result,
          id: result.id,
          name: result.name,
          leader: result.leader,
          members: result.members ?? [],
          associatedFirms: result.associatedFirms ?? [],
          isVisible: result.isVisible ?? false,
        });

        dispatch(
          marketplaceApi.util.upsertQueryData(
            "getMarketplaceTeamById",
            { teamId: teamJSON.id },
            teamJSON,
          ),
        );
        continue;
      }

      if (result.type === "Company") {
        const companyJSON = CompanySchema.validateJSON({
          ...result,
          id: result.id,
          legalEntityId: result.legalEntityId,
          name: result.name,
          website: result.website,
          contactId: result.contact?.userId,
          isVisible: result.isVisible ?? false,
        });

        dispatch(
          marketplaceApi.util.upsertQueryData(
            "getMarketplaceCompanyById",
            { companyId: companyJSON.id },
            companyJSON,
          ),
        );
      }
    } catch {
      continue;
    }
  }
}

export const marketplaceSearchCacheHydrationMiddleware: Middleware = ({ dispatch }) => (next) => (action) => {
  const result = next(action);

  if (!isMarketplaceSearchFulfilledAction(action as UnknownAction)) {
    return result;
  }

  const payload = (action as { payload: SearchResults<MarketplaceSearchInfoJSON> }).payload;
  hydrateMarketplaceSearchCaches(payload, dispatch as AppDispatch);

  return result;
};
